import React, { useId } from 'react';
import { COW, CutItem } from '../../utils/guide';
import classes from './CowDiagram.module.css';

interface CowDiagramProps {
  item: CutItem;
  /** Section numbers to light, for a menu word that can be several cuts */
  lit?: number[];
}

/**
 * The little cow: the section a cut comes from lit in orange (a cut with its own
 * spot shows the section softly and the spot strongly), or the organ for offal.
 */
export const CowDiagram: React.FC<CowDiagramProps> = ({ item, lit }) => {
  const uid = useId().replace(/:/g, '');
  const uses = (cls: string) => <g className={cls}>{COW.SHAPES.map((_, i) => <use key={i} href={`#${uid}s${i}`} />)}</g>;
  const offal = item.kind === 'offal';

  let inner: React.ReactNode;
  if (offal) {
    inner = Object.entries(COW.OFFAL_SHAPES).map(([id, d]) => (
      <path key={id} d={d} className={`${classes.part} ${COW.INNER.includes(id) ? classes.inner : ''} ${id === item.id ? classes.on : ''}`} />
    ));
  } else {
    const regions = lit || [item.kind === 'cut' ? item.r : item.kind === 'region' ? item.n : 0];
    const spot = item.kind === 'cut' ? COW.SPOTS.find(([no]) => no === item.no) : undefined;
    const leg = item.kind === 'cut' ? item.leg : undefined;
    inner = (
      <>
        {COW.PARTS.map(([n, d, partLeg], i) => {
          const on = regions.includes(n) && (!leg || partLeg === leg);
          return <path key={i} d={d} className={`${classes.part} ${on ? (spot ? classes.soft : classes.on) : ''}`} />;
        })}
        {spot && <path d={spot[1]} className={`${classes.part} ${classes.on}`} />}
      </>
    );
  }
  const label = item.kind === 'word' ? 'Where these cuts come from' : offal ? `Where ${item.en.toLowerCase()} sits, roughly` : 'Where it comes from on the cow';

  return (
    <svg className={classes.cow} viewBox="30 10 915 610" role="img" aria-label={label}>
      <defs>
        {COW.SHAPES.map((d, i) => <path key={i} id={`${uid}s${i}`} d={d} />)}
        <clipPath id={`${uid}clip`}>{COW.SHAPES.map((_, i) => <use key={i} href={`#${uid}s${i}`} />)}</clipPath>
      </defs>
      <path className={`${classes.tail} ${offal && item.id === 'teru' ? classes.on : ''}`} d={COW.TAIL} />
      <path className={classes.ink} d={COW.TUFT} />
      {uses(classes.silOuter)}
      {uses(classes.silFill)}
      {COW.HOOVES.map(([a, b]) => (
        <path key={a} className={classes.ink} d={`M${a} 594 L${b} 594 L${b} 604 Q${b} 612 ${b - 8} 612 L${a + 6} 612 Q${a - 2} 612 ${a} 604 Z`} />
      ))}
      <g clipPath={`url(#${uid}clip)`}>{inner}</g>
      <circle className={classes.ink} cx="158" cy="124" r="7" />
    </svg>
  );
};
